import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import intl from 'react-intl-universal';
import { useNavigate, useParams } from 'react-router-dom';

import { InfoOutlined as IconInfo, EditOutlined as IconEdit } from '@mui/icons-material';
import LinearProgress from '@mui/material/LinearProgress';

import saveAs from 'file-saver';
import { ImmutableArray } from 'seamless-immutable';

import { User } from '@/models/permission/AuthorityModel.ts';
import { Board, DeliveryConfig } from '@/models/BoardModel.ts';
import { Card, Field } from '@/models/card/CardModel.ts';

import { actions as boardActions, selectActiveBoard, selectRoomMembers } from '../../ducks/Boards';
import {
  actions as dActions,
  selectDeliveries,
  selectIsLoadingDeliveries,
  selectLastActiveDelivery,
  selectListViewConfig,
} from '../../ducks/Deliveries';
import { exportContent } from '../../services/Export';

import { alertAction } from '../../thunks/Alerts';

import * as deliveryActions from '../../thunks/Deliveries';

import ListBoard from './ListBoard';

import { generateExportRequest } from '../../utils/ExportUtil';
import {
  toPrimitiveFieldValue,
  itemToRow,
  getCellComponentRenderer,
  getCardInfo,
  getEntryTitle,
} from '../../utils/FieldUtil';
import { deliveryFieldsToColumns, generateNumberOfCardsMessage, STATUS_COLUMN } from '../../utils/ListViewUtil';
import { canEditDelivery } from '../../utils/PermissionUtils';

import { LIST_DELIVERY_VIEW_MODE } from '../../utils/ViewModeUtil';

import DeleteDeliveryDialog from '../../common/DeleteCardItemDialog';
import DeleteIconButton from '../../common/DeleteIconButton';
import Error from '../../common/Error';
import PageWrapper from '../../common/PageWrapper';
import { Cell } from '../../common/table';
import ToggleIconButton from '../../common/ToggleIconButton';
import SubscriptionContainer from '../../common/SubscriptionContainer';

let searchTerm = '';

const mapStateToProps = () =>
  createStructuredSelector({
    activeBoard: selectActiveBoard(),
    isLoadingDeliveries: selectIsLoadingDeliveries(),
    deliveries: selectDeliveries(),
    roomMembers: selectRoomMembers(),
    lastActiveDelivery: selectLastActiveDelivery(),
    listViewConfig: selectListViewConfig(),
  });

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      ...deliveryActions,
      ...boardActions,
      ...dActions,
      cleanUp: dActions.cleanUp,
    },
    dispatch,
  ),
});

const ListDeliveryContainer = ({
  activeBoard,
  deliveries,
  lastActiveDelivery,
  isLoadingDeliveries,
  roomMembers,
  listViewConfig: initialViewConfig,
  actions,
}: {
  activeBoard: Board;
  deliveries: ImmutableArray<Card>;
  lastActiveDelivery: Card;
  isLoadingDeliveries: boolean;
  roomMembers: User[];
  listViewConfig: any;
  actions: any;
}) => {
  const navigate = useNavigate();
  const { boardId, communityId, roomId } = useParams();

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [filters, setFilters] = useState([]);
  const [viewConfig, setViewConfig] = useState(initialViewConfig);
  const [deliveryToDelete, setDeliveryToDelete] = useState<Card | null>(null);

  useEffect(() => {
    actions.fetchDeliveries({ boardId, communityId, roomId });

    searchTerm = localStorage.getItem('searchTerm') || '';
    setFilters(JSON.parse(localStorage.getItem('filters') || '[]'));
  }, [boardId, communityId, roomId, actions]);

  useEffect(() => {
    return () => {
      if (viewConfig) {
        actions.setListViewConfig(viewConfig);
      }
    };
  }, [viewConfig, actions]);

  const handleSortChange = (sorter: any) => {
    setViewConfig((prev: any) => ({ ...prev, sorter }));
  };

  const handleColumnOrderChange = (columnOrder: any) => {
    setViewConfig((prev: any) => ({ ...prev, columnOrder }));
  };

  const handleColumnVisibilityChange = (visibleColumns: any) => {
    setViewConfig((prev: any) => ({ ...prev, visibleColumns }));
  };

  const handleSearch = (searchTerm: any) => {
    localStorage.setItem('searchTerm', searchTerm);
  };

  const handleFilter = (filters: any) => {
    localStorage.setItem('filters', JSON.stringify(filters));
    setFilters(filters);
  };

  const handleDeliveryEditClick = (deliveryId: string) => {
    actions.setLastActiveDelivery({ lastActiveDelivery: deliveryId });
    navigate(`/${communityId}/${roomId}/boards/${boardId}/deliveries/${deliveryId}?view=${LIST_DELIVERY_VIEW_MODE}`);
  };

  const handleDeliveryCreateClick = () =>
    navigate(`/${communityId}/${roomId}/boards/${boardId}/create-delivery?view=${LIST_DELIVERY_VIEW_MODE}`);

  const handleDelete = () => {
    setOpenDeleteDialog(false);

    actions.deleteDelivery({ communityId, roomId, boardId, delivery: deliveryToDelete }).then((action: any) =>
      alertAction({
        action,
        success: intl.get('board.tooltips.delivery.delete.success.message'),
        error: intl.get('board.tooltips.delivery.delete.error.message'),
        onSuccess: () => {},
      }),
    );
  };

  const calculateStatus = (row: any, fieldId: string) =>
    row.fields.find((field: Field) => field.id === fieldId && field.value)
      ? intl.get('delivery.status.delivered')
      : intl.get('delivery.status.not_delivered');

  const rowToDeliveryInfo = (row: any) => {
    const cardInfo = row.fields.reduce((deliveryInfo: any, field: Field) => {
      deliveryInfo[field.id] = toPrimitiveFieldValue(field, roomMembers);
      return deliveryInfo;
    }, {});
    return cardInfo.merge({ [STATUS_COLUMN]: row[STATUS_COLUMN], ...getCardInfo(row, roomMembers) });
  };

  const deliveriesToRows = (deliveries: ImmutableArray<Card>, deliveryConfig: DeliveryConfig) =>
    deliveries.asMutable().map((delivery) => {
      const row: any = itemToRow({ item: delivery, fieldConfig: deliveryConfig, roomMembers });
      row[STATUS_COLUMN] = calculateStatus(delivery, deliveryConfig.delivery_met_date_field);
      return row;
    });

  const handleClickOpenDialog = (delivery: Card) => {
    setDeliveryToDelete(delivery);
    setOpenDeleteDialog(true);
  };

  const renderDeliveryLinkButton = ({ row }: { row: any }) => {
    const canEdit = canEditDelivery(row.permissions);

    return (
      <Cell>
        <ToggleIconButton
          tooltipText={intl.get(
            canEdit ? 'board.views.list.tooltips.edit_delivery' : 'board.views.list.tooltips.view_delivery',
          )}
          generalIcon={canEdit ? <IconEdit /> : <IconInfo />}
          hoverIcon={canEdit ? <IconEdit /> : <IconInfo />}
          disabled={false}
          onClick={() => handleDeliveryEditClick(row.id)}
        />
        <DeleteIconButton
          tooltipText={intl.get('board.tooltips.delivery.delete.question.title')}
          onClick={() => handleClickOpenDialog(row)}
          disabled={!row.permissions.DELETE}
        />
      </Cell>
    );
  };

  const handleExportClick = async ({ columns, items }: { columns: any; items: any }) => {
    const exportRequest = generateExportRequest({ columns, items });
    const folderName = activeBoard.delivery_config.delivery_folder_name;

    const response = await exportContent({ communityId, roomId, boardId, exportRequest });
    return await Promise.resolve(saveAs(response.data, `${folderName}.csv`));
  };

  if (isLoadingDeliveries) {
    return <LinearProgress />;
  }

  return (
    <PageWrapper title={intl.get('app_bar.delivery_list_view')}>
      {activeBoard.delivery_config ? (
        <React.Fragment>
          <ListBoard
            lastActiveItem={lastActiveDelivery}
            board={activeBoard}
            items={deliveriesToRows(deliveries, activeBoard.delivery_config)}
            height={window.innerHeight - 255}
            style={{ marginBottom: 15 }}
            columns={deliveryFieldsToColumns(activeBoard.delivery_config)}
            renderCellComponent={getCellComponentRenderer(activeBoard.delivery_config.fields, roomMembers)}
            editing={{ renderLinkButtons: renderDeliveryLinkButton, width: 110 }}
            rowToInfo={rowToDeliveryInfo}
            readOnly={!activeBoard.delivery_config.permissions.ADD_DELIVERY}
            onItemCreateClick={handleDeliveryCreateClick}
            tooltips={{ create: intl.get('board.tooltips.add_delivery') }}
            subscription={<SubscriptionContainer id={activeBoard.delivery_config.delivery_folder_id} />}
            filters={filters}
            onFiltersChange={handleFilter}
            onSearch={handleSearch}
            onSortChange={handleSortChange}
            onColumnOrderChange={handleColumnOrderChange}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            searchTerm={searchTerm}
            selection={{
              enableSelection: true,
              helperText: intl.get('board.views.list.tooltips.cards.export.helper_text'),
              numberOfItemsMessage: generateNumberOfCardsMessage,
            }}
            sorter={viewConfig.sorter}
            defaultSorter={[{ columnName: getEntryTitle(activeBoard.delivery_config.fields) }]}
            columnOrder={viewConfig.columnOrder}
            visibleColumns={viewConfig.visibleColumns}
            onExportClick={handleExportClick}
          />
          <DeleteDeliveryDialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
            onDelete={handleDelete}
            titles={{
              deleteDialogTitle: intl.get('board.tooltips.delivery.delete.question.title'),
              deleteDialogMessage: intl.get('board.tooltips.delivery.delete.question.message'),
            }}
          />
        </React.Fragment>
      ) : (
        <Error text={intl.get('delivery.not_found')} />
      )}
    </PageWrapper>
  );
};

const ConnectedListDeliveryContainer = connect(mapStateToProps, mapDispatchToProps)(ListDeliveryContainer);
export default ConnectedListDeliveryContainer;
